import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { catchError, map, Observable, of } from "rxjs";
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import {
	FUNCTIONAL_PRODUCT_KEYS,
	Product,
	ProductKey,
	ProductOptions,
	PROFESSIONAL_PRODUCT_KEYS,
	ToothArea,
} from "libs/product/src/lib";
import { saveAs } from "file-saver";
import {
	SapAddress,
	SapShippingCostItem,
	SapShippingMethod,
	SapCostEstimate,
	mapDocNumPrefixToPdfFilename,
	SapLanguageCode,
} from "../models/sap.model";
import { DataTransferMethod } from "libs/treatments/src/lib/models/treatment.model";
import { ProductUtilitiesService } from "libs/product/src/lib/services/product-utilities.lib.service";
import { AdminPreviewQuotation } from "libs/sap/src/lib/interfaces/sap.preview-quotation.interface";
import { SapQuotationViewType } from "libs/sap/src/lib/interfaces/sap.preview-quotation.interface";
import { FUNCTIONAL_CANCELLATION_COST_ESTIMATE, PROFESSIONAL_CANCELLATION_COST_ESTIMATE } from "libs/order/src/lib/constants/cost-estimate-fixed-prices";

export type SapAddressesResponse = {
	billingAddresses: SapAddress[],
	shippingAddresses: SapAddress[]
}

@Injectable({
	providedIn: "root"
})
export class SapService {
	private http = inject(HttpClient);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);

	baseUrl: string;
	sapApiUrl: string;

	constructor() {
		this.baseUrl = this.config.apiUrl;
		this.sapApiUrl = this.config.sapApiUrl;
	}

	public getCustomerAddresses(): Observable<SapAddressesResponse>{
		return this.http.get<SapAddressesResponse>(`${this.baseUrl}/customer/addresses`);
	}

	public getShippingCosts(countryCode: string): Observable<SapShippingCostItem[]> {
		const queryParams = {
			country: countryCode.toUpperCase()
		}
		return this.http.get<SapShippingCostItem[]>(
			`${this.baseUrl}/ordering/shippingCost`,  {params: queryParams, withCredentials: true}
		);
	}
	public getEstimatedCosts(
		productKey: ProductKey,
		shippingMethod: SapShippingMethod,
		countryCode: string,
		transferMethod: DataTransferMethod,
		uj: ToothArea,
		lj: ToothArea,
		benefit?: string
	 ) : Observable<SapCostEstimate>{
			const queryParams = {
				country: countryCode.toUpperCase(),
				method: shippingMethod,
				productKey: productKey,
				transferMethod: transferMethod,
				uj: uj,
				lj: lj,
				benefit: benefit
			};
			//delete benefit if it's not set, otherwise it will be passed as `benefit=undefined` to the backend
			if(!benefit){
				delete queryParams.benefit;
			}

			return this.http.get<SapCostEstimate>(
				`${this.baseUrl}/ordering/costEstimate`,
				{ params: queryParams, withCredentials: true }
			);
		}

	public getPriceForCancellation(productKey: ProductKey)
		: Observable<SapCostEstimate | null> {
		if( PROFESSIONAL_PRODUCT_KEYS.includes(productKey)){
			return of(PROFESSIONAL_CANCELLATION_COST_ESTIMATE);
		}
		if( FUNCTIONAL_PRODUCT_KEYS.includes(productKey)){
			return of(FUNCTIONAL_CANCELLATION_COST_ESTIMATE);
		}
		return of(null)
	}

	public getPriceForQuotation(
		treatmentId: string )
		: Observable<SapCostEstimate> {
		return this.http.get<SapCostEstimate>(
				`${this.baseUrl}/ordering/finalPrice/treatment/${treatmentId}`,
			);
	}

	public getQuotationData(docEntry: number) : Observable<AdminPreviewQuotation> {
		return this.http.get<any>(`${this.baseUrl}/b1sl/quotations/${docEntry}`, {
			params: { viewType: SapQuotationViewType.TMAdmin }
		});
	}

	/**
	 * Downloads and saves the cost estimate pdf in the default browser location.
	 * @param treatmentId
	 * @param docNum Id representing the pdf document in SAP
	 */
	public getCostEstimatePdf(
		treatmentId: string,
		docNum: string,
		languageCode: SapLanguageCode = SapLanguageCode.ENGLISH
	): Observable<boolean> {
		return this.http.get<string>(`${this.baseUrl}/pdf/costEstimate/treatment/${treatmentId}`).pipe(
			map((response: string) => {
				const blob = this.convertBase64ToPdfBlob(response);
				const filename = mapDocNumPrefixToPdfFilename(docNum, undefined, languageCode);
				saveAs(blob, filename);
				return true;
			}),
			catchError((error) => {
				console.error("Error caught while retrieving cost estimate pdf", error);
				return of(false);
			})
		);
	}

	/**
	 * See https://stackoverflow.com/a/16245768
	 * @param base64String
	 * @returns The base64 string as 'Blob'
	 */
	private convertBase64ToPdfBlob(base64String: string) {
		const byteCharacters = atob(base64String);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: "application/pdf" });
	}

	/**
	 * Call TM-Server and request to create an SAP order from quotation
	 *
	 * @param docEntry
	 * @param caseworker
	 * @param completionDate
	 * @returns Response of the order creation call
	 */
	public createOrder(docEntry: number, caseworker: string, completionDate: string, productionShippingNote: string | null) {
		const payload = {
			"DocEntry": docEntry,
			"Caseworker": caseworker,
			"CompletionDate": completionDate,
		};
		if(productionShippingNote){
			payload["ProductionShippingNote"] = productionShippingNote;
		}
		return this.http.post(`${this.sapApiUrl}/orders`, payload, { withCredentials: true });
	}

	/**
	 * Call TM-Server and request to update product on an existing SAP quotation
	 *
	 * @param docEntry
	 * @param oldProduct
	 * @param newProduct
	 * @returns
	 */
	public updateProductConfigInSapQuotation(docEntry: number, oldProduct: Product, newProduct: Product){
		if(oldProduct.key !== newProduct.key){
			throw new Error("Changing base product is not supported");
		}
		const oldProductOptions = oldProduct.options as ProductOptions;
		const newProductOptions = newProduct.options as ProductOptions;
		const oldProductJaws = ProductUtilitiesService.getSapJaws(ProductUtilitiesService.getDentalRange(oldProductOptions));
		const newProductJaws = ProductUtilitiesService.getSapJaws(ProductUtilitiesService.getDentalRange(newProductOptions));
		const payload = {
			ProductKey: oldProduct.key,
			OldProductOptions: {
				Jaws: oldProductJaws,
				TransferMethod: oldProductOptions.dataTransfer,
				DentalRange: {
					UJ: oldProductOptions.uj,
					LJ: oldProductOptions.lj,
				},
			},
			NewProductOptions: {
				Jaws: newProductJaws,
				TransferMethod: newProductOptions.dataTransfer,
				DentalRange: {
					UJ: newProductOptions.uj,
					LJ: newProductOptions.lj,
				},
			},
		}
		return this.http.post(
			`${this.sapApiUrl}/quotations/${docEntry}/product`,  payload, {withCredentials: true}
		);
	}
}
